import { useState, useEffect, useCallback, useMemo } from 'react'
import { cn } from '@utils'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import useFormPersist from 'react-hook-form-persist'
import { zodResolver } from '@hookform/resolvers/zod'
// Intentionally not importing 'solvpath-microform' at module top — loaded lazily
// from useEffect so bundling failures don't crash the whole checkout route.
import Modal from '@/components/ui/Modal'
import { sessionCartService } from '@/services/campaignService'
import { Minus, Plus, Trash2, Lock, ArrowRight, ShoppingCart, ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import './checkout.css'
import '@styles/payment-info.css'

// Stores and services
import { useSessionStore } from '@stores/useSessionStore'
import { useUIStore } from '@stores/useUIStore'
import { useCartStore } from '@stores/useCartStore'

// Services and utilities
import { paymentService } from '@services/paymentService'

import { isCountryUSOrCA, isFieldRequired, formatZipField } from '@utils/validation'

import {
  calculatePrice,
  formatCurrency,
  getItemVariants,
  getItemImageUrl,
  setFallbackImageUrl,
  detectCardBrand,
  OrderPaymentOptions,
} from './checkout/checkoutHelpers'
import type { PaymentSource, ExtendedCartItem } from './checkout/checkoutHelpers'
import { cardSchema, customerSchema } from './checkout/checkoutSchemas'
import type { CardFormValues, CustomerFormValues } from './checkout/checkoutSchemas'
import CheckoutBillingSection from './checkout/CheckoutBillingSection'
import CheckoutShippingSection from './checkout/CheckoutShippingSection'
import CheckoutPaymentSection from './checkout/CheckoutPaymentSection'

export interface CheckoutProps {
  onSubmit: (checkoutInput: any, params: any) => void
  onBack?: () => void // Make onBack optional
  // Renders inside a narrow container (OMP drawer): one column, order summary
  // always collapsible, and no route changes of its own.
  compact?: boolean
  // Stack the form over the summary. Defaults to `compact`; a wide centred modal
  // passes false to keep the two-column desktop layout.
  singleColumn?: boolean
  // The session cart returns campaign_product without a nested product, so an
  // embedded host that already knows the item can resolve its image, and retry
  // other sizes on error the way its own cards do.
  resolveImage?: (item: any) => string | undefined
  onImageError?: (event: React.SyntheticEvent<HTMLImageElement>, item: any) => void
  // The step to read valid_inputs from. Defaults to the store; OMP passes the
  // pinned action step so state.session can stay on the portal step.
  session?: any
  // compact mode routes every would-be navigation here instead of the router.
  onLeave?: (target: 'products' | 'session' | { productId: number | string; slug?: string }) => void
}

const Checkout = ({
  onSubmit,
  compact = false,
  singleColumn = compact,
  resolveImage,
  onImageError,
  session: sessionProp,
  onLeave,
}: CheckoutProps) => {
  // Zustand store hooks
  const { session: storeSession, isSessionUserKnown } = useSessionStore()
  // sessionProp supplies valid_inputs; customer can live on either step.
  const activeStep = sessionProp ?? storeSession
  const session = useMemo(
    () =>
      activeStep
        ? { ...activeStep, customer: (activeStep as any).customer ?? (storeSession as any)?.customer }
        : activeStep,
    [activeStep, storeSession]
  )
  const campaign = useSessionStore(s => s.campaign)
  const { setLoading: setGlobalLoading, setError, setCriticalError } = useUIStore()
  const setLoading = useCallback(
    (value: boolean) => {
      if (!compact) {
        setGlobalLoading(value)
      }
    },
    [compact, setGlobalLoading]
  )
  const isLoading = useUIStore(s => s.isLoading)
  const { cart, shipping, tax, gateway, checkoutErrors, setCheckoutErrors } = useCartStore()

  const routerNavigate = useNavigate()
  const leave = useCallback(
    (target: 'products' | 'session' | { productId: number | string; slug?: string }, opts?: { replace?: boolean }) => {
      if (compact) {
        onLeave?.(target)
        return
      }
      if (target === 'products') {
        routerNavigate('/products')
      } else if (target === 'session') {
        routerNavigate('/session', { replace: !!opts?.replace })
      } else {
        routerNavigate(`/products/${target.productId}/${target.slug || target.productId}`)
      }
    },
    [compact, onLeave, routerNavigate]
  )

  // Remove-item confirmation dialog state
  const [removeIndex, setRemoveIndex] = useState<number | null>(null)
  const [removing, setRemoving] = useState<boolean>(false)

  // Which cart row is currently syncing to the API — shows a spinner + disables buttons
  const [updatingIndex, setUpdatingIndex] = useState<number | null>(null)

  // State management
  const [checkoutInput, setCheckoutInput] = useState<any>(null)
  const [updatedCart, setUpdatedCart] = useState<ExtendedCartItem[]>(
    cart
      ? cart.map(
          item =>
            ({
              ...item,
              campaign_product: {
                ...item.campaign_product,
                currency: (item.campaign_product as any)?.currency || 'USD',
              },
            }) as ExtendedCartItem
        )
      : []
  )

  // Customer / address form — RHF + Zod
  const {
    register: registerCustomer,
    watch: watchCustomer,
    setValue: setCustomerValue,
    trigger: triggerCustomer,
    setError: setCustomerError,
    formState: { errors: customerErrors, isValid: isCustomerValid },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    mode: 'onChange',
    defaultValues: {
      first_name: '',
      last_name: '',
      company: '',
      email: '',
      phone: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      ship_first_name: '',
      ship_last_name: '',
      ship_address1: '',
      ship_address2: '',
      ship_city: '',
      ship_state: '',
      ship_zip: '',
      ship_country: 'US',
    },
  })
  const customer = watchCustomer()

  useFormPersist(compact ? 'omp-checkout-customer' : 'checkout-customer', {
    watch: watchCustomer,
    setValue: setCustomerValue,
    storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
  })

  // Credit card form — RHF + Zod (mirrors PaymentUpdater validation/formatting)
  const {
    watch: watchCard,
    setValue: setCardValue,
    trigger: triggerCard,
    setError: setCardError,
    formState: { errors: cardErrors, isValid: isCardValid },
  } = useForm<CardFormValues>({
    resolver: zodResolver(cardSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      number: '',
      expiry: '',
      cvv: '',
    },
  })
  const creditCard = watchCard()
  const cardBrand = detectCardBrand(creditCard.number || '')

  useFormPersist(compact ? 'omp-checkout-card' : 'checkout-card', {
    watch: watchCard,
    setValue: setCardValue,
    storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
    exclude: ['cvv'],
  })

  // UI state
  const [showOrderSummary, setShowOrderSummary] = useState<boolean>(false)
  // fieldErrors removed — validation via customerErrors (RHF + Zod)
  const [showShipEdit, setShowShipEdit] = useState<boolean>(true)
  const [showBillEdit, setShowBillEdit] = useState<boolean>(true)
  const [showSourceEdit, setShowSourceEdit] = useState<boolean>(true)
  const [billShipSame, setBillShipSame] = useState<boolean>(true)

  // Payment source state
  const [orderPaymentSource, setOrderPaymentSource] = useState<OrderPaymentOptions>(OrderPaymentOptions.New)
  const [paymentSource, setPaymentSource] = useState<PaymentSource | null>(null)
  const [paymentSourceOptions, setPaymentSourceOptions] = useState<PaymentSource[]>([])

  // Computed values
  const totalPrice = cart
    ? cart
        .reduce((total, item) => {
          return total + calculatePrice(item)
        }, 0)
        .toFixed(2)
    : '0.00'

  const totalPriceIncTax = () => {
    let total = parseFloat(totalPrice) + parseFloat(tax?.toString() || '0')
    if (shipping && shipping > 0) {
      total += shipping
    }
    return total.toFixed(2)
  }

  const currency = updatedCart.length > 0 ? updatedCart[0].campaign_product.currency || 'USD' : 'USD'

  const isCustomerUpdated = (): boolean => {
    if (!session?.customer) {
      return false
    }
    return Object.entries(customer).some(([key, value]) => {
      const sessionCustomer = session.customer as any
      return value !== sessionCustomer[key]
    })
  }

  const guaranteeDays = (campaign as any)?.guarantee_days ?? 0
  const showSatisfactionGuarantee = guaranteeDays > 0

  // Utility functions
  const getPrice = (item: any) => {
    const price = calculatePrice(item)
    return price !== null && !isNaN(price) ? price.toFixed(2) : '0'
  }

  const getImageUrl = (item: any) => {
    return resolveImage?.(item) || getItemImageUrl(item)
  }

  const setAltImg = (event: React.SyntheticEvent<HTMLImageElement>, item?: any) => {
    if (onImageError) {
      onImageError(event, item)
      return
    }
    setFallbackImageUrl(event)
  }

  // Vue parity: a section header shows Edit when collapsed and Save when open;
  // Save validates only that section and collapses it if the section is valid.
  const toggleEditing = async (name: 'bill' | 'ship' | 'source') => {
    if (name === 'bill') {
      if (!showBillEdit) {
        setShowBillEdit(true)
      } else if (await validateBillingFields()) {
        setShowBillEdit(false)
      }
      return
    }
    if (name === 'ship') {
      if (!showShipEdit) {
        setShowShipEdit(true)
      } else if (await validateShippingFields()) {
        setShowShipEdit(false)
      }
      return
    }
    if (!showSourceEdit) {
      setShowSourceEdit(true)
    } else if (await validateCreditCardInfo()) {
      setShowSourceEdit(false)
    }
  }

  const validateBillingFields = () => {
    const fields: Array<keyof CustomerFormValues> = [
      'first_name',
      'last_name',
      'email',
      'phone',
      'address1',
      'city',
      'zip',
      'country',
    ]
    if (isCountryUSOrCA(customer.country)) {
      fields.push('state')
    }
    if (isFieldRequired('company', customer.country)) {
      fields.push('company')
    }
    return triggerCustomer(fields)
  }

  const validateShippingFields = () => {
    if (billShipSame) {
      return Promise.resolve(true)
    }
    const fields: Array<keyof CustomerFormValues> = [
      'ship_first_name',
      'ship_last_name',
      'ship_address1',
      'ship_city',
      'ship_zip',
      'ship_country',
    ]
    if (isCountryUSOrCA(customer.ship_country)) {
      fields.push('ship_state')
    }
    return triggerCustomer(fields)
  }

  const validateCreditCardInfo = async (): Promise<boolean> => {
    if (orderPaymentSource !== OrderPaymentOptions.New) {
      return true
    }
    return triggerCard()
  }

  const updateShippingAddress = useCallback(
    (copyFromBilling: boolean) => {
      if (!copyFromBilling) {
        return
      }
      const pairs = ['address1', 'address2', 'city', 'state', 'zip', 'first_name', 'last_name', 'country'] as const
      pairs.forEach(f => {
        const value = customer[f as keyof CustomerFormValues] as string | undefined
        setCustomerValue(`ship_${f}` as keyof CustomerFormValues, value || '')
      })
    },
    [customer, setCustomerValue]
  )

  // Cart store actions — used by updateQuantity / remove flows
  const { setCart, updateSessionCart } = useCartStore()

  const updateQuantity = async (index: number, quantity: number) => {
    const maxQuantity = updatedCart[index]?.campaign_product?.max_quantity || 999
    const clamped = Math.max(1, Math.min(quantity, maxQuantity))

    const newCart = updatedCart.map((item, i) => (i === index ? { ...item, quantity: clamped } : item))
    setUpdatedCart(newCart)
    // Sync to store immediately so store subscribers see the change
    setCart(newCart)

    if (!session?.id) {
      return
    }
    // Show spinner next to the qty controls while the cart API is in flight
    setUpdatingIndex(index)
    try {
      await updateSessionCart(session.id, {
        ship_city: customer.ship_city,
        ship_state: customer.ship_state,
        ship_zip: customer.ship_zip,
      })
    } catch {
      /* surface handled elsewhere */
    } finally {
      setUpdatingIndex(null)
    }
  }

  // Open confirmation dialog for removing an item
  const handleRemoveClick = (index: number) => {
    setRemoveIndex(index)
  }

  // Confirmed removal — filter the item; if cart becomes empty, clear + go back to products
  const confirmRemove = async () => {
    if (removeIndex === null || removing) {
      return
    }
    const idx = removeIndex
    const isLast = updatedCart.length <= 1

    const newCart = updatedCart.filter((_, i) => i !== idx)
    setUpdatedCart(newCart)
    setCart(newCart)

    if (isLast) {
      // Last item — keep the modal open showing the loader while we clear + redirect
      setRemoving(true)
      useCartStore.getState().clearCart()
      if (session?.id) {
        try {
          await sessionCartService.clearSessionCart(session.id)
        } catch {
          /* best-effort */
        }
      }
      leave('products')
      // Component will unmount — no need to reset state
      return
    }

    // Non-last item — close the modal immediately, sync the cart in the background
    setRemoveIndex(null)
    if (session?.id) {
      updateSessionCart(session.id, {
        ship_city: customer.ship_city,
        ship_state: customer.ship_state,
        ship_zip: customer.ship_zip,
      }).catch(() => {
        /* best-effort */
      })
    }
  }

  const countryChanged = () => {
    setCustomerValue('state', '')
    setCustomerValue('zip', '')
  }

  const shipCountryChanged = () => {
    setCustomerValue('ship_state', '')
    setCustomerValue('ship_zip', '')
  }

  const zipFormatHandler = (value: string, isShip: boolean = false) => {
    const country = isShip ? customer.ship_country : customer.country
    return formatZipField(value, country)
  }

  // Checkout submission
  const handleCheckout = async () => {
    const isBillingValid = await validateBillingFields()
    const isShippingValid = await validateShippingFields()
    const isCreditCardValid = await validateCreditCardInfo()

    if (!isShippingValid) {
      setShowShipEdit(true)
    }
    if (!isBillingValid) {
      setShowBillEdit(true)
    }
    if (!isBillingValid || !isShippingValid || !isCreditCardValid) {
      return
    }

    const cartData = cart?.map(item => {
      let bundledVariants = null
      const extendedItem = item as ExtendedCartItem
      if (extendedItem.campaign_product.bundled_variants?.length) {
        bundledVariants = extendedItem.campaign_product.bundled_variants.map(bundle => ({
          variant: bundle.variant.id,
          quantity: bundle.quantity,
        }))
      }

      return {
        campaign_product: item.campaign_product.id,
        variant: item.variant?.id || null,
        quantity: item.quantity,
        bundled_variants: bundledVariants,
      }
    })

    const params: any = { items: cartData }

    if (isSessionUserKnown() && isCustomerUpdated()) {
      params.customer = customer
    } else if (!isSessionUserKnown()) {
      params.customer = customer
    }

    if (orderPaymentSource === OrderPaymentOptions.New) {
      const [expMonthStr, expYearStr] = (creditCard.expiry || '').split('/')
      let exp_year = (expYearStr || '').trim()
      if (exp_year.length === 2 && /^\d+$/.test(exp_year)) {
        exp_year = new Date().getFullYear().toString().slice(0, 2) + exp_year
      }
      const rawMonth = (expMonthStr || '').trim()
      const exp_month = /^\d+$/.test(rawMonth) ? rawMonth.padStart(2, '0') : rawMonth
      params.card = {
        title: creditCard.title,
        number: creditCard.number.replace(/\s/g, ''),
        exp_month,
        exp_year,
        cvv_code: creditCard.cvv,
      }
    }

    if (gateway) {
      params.gateway = gateway.id
    }

    setCheckoutErrors(null)
    onSubmit(checkoutInput, params)
  }

  // Effects
  useEffect(() => {
    useCartStore.getState().setCartOpen(false)
  }, [])

  useEffect(() => {
    if (session?.valid_inputs?.[0]) {
      setCheckoutInput(session.valid_inputs[0])
    }
  }, [session])

  useEffect(() => {
    if (!cart || cart.length) {
      return
    }
    if (!(session as any)?.is_legacy) {
      const goHomeAndNavigate = async () => {
        try {
          await useSessionStore.getState().goHome()
        } finally {
          leave('session', { replace: true })
        }
      }
      goHomeAndNavigate()
    } else {
      leave('products')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Fires only when the cart size changes; the other values are read, not tracked.
  }, [cart?.length])

  useEffect(() => {
    setUpdatedCart(
      cart
        ? cart.map(
            item =>
              ({
                ...item,
                campaign_product: {
                  ...item.campaign_product,
                  currency: (item.campaign_product as any)?.currency || 'USD',
                },
              }) as ExtendedCartItem
          )
        : []
    )
  }, [cart])

  useEffect(() => {
    if (billShipSame && !isSessionUserKnown()) {
      updateShippingAddress(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Runs on the address/session transition only; adding the setters would re-run on every render.
  }, [billShipSame, customer.address1, customer.city, customer.state, customer.zip, customer.country])

  useEffect(() => {
    if (isSessionUserKnown() && session?.customer) {
      const sc = session.customer as any
      const fields: Array<keyof CustomerFormValues> = [
        'first_name',
        'last_name',
        'company',
        'email',
        'phone',
        'address1',
        'address2',
        'city',
        'state',
        'zip',
        'country',
        'ship_first_name',
        'ship_last_name',
        'ship_address1',
        'ship_address2',
        'ship_city',
        'ship_state',
        'ship_zip',
        'ship_country',
      ]
      fields.forEach(key => {
        const value = sc[key]
        if (key === 'country' || key === 'ship_country') {
          setCustomerValue(key, value?.toUpperCase() || 'US')
        } else {
          setCustomerValue(key, value || '')
        }
      })
      // Re-run validation across the form so isValid flips true and the
      // Complete-Order button enables. setValue alone does not validate.
      triggerCustomer()

      setBillShipSame(false)
      setShowShipEdit(false)
      setShowBillEdit(false)
      setShowSourceEdit(false)
      setLoading(true)

      const loadPaymentSources = async () => {
        try {
          const paymentSources = await paymentService.getPaymentSources()
          setPaymentSource(null)
          let source = paymentSources.find(ps => ps.is_default)
          if (!source && paymentSources.length >= 1) {
            source = paymentSources[0]
          }

          if (source) {
            setOrderPaymentSource(OrderPaymentOptions.Default)
            setPaymentSource({
              id: source.id,
              isDefault: source.is_default || false,
              canUpdate: source.can_update,
              isCard: true,
              source: 'Credit Card',
              type: source.cc_type || 'unknown',
              last4: source.cc_last4 || '****',
              expDate: source.cc_exp_date || '',
            })
            setPaymentSourceOptions([
              {
                id: source.id,
                isDefault: source.is_default || false,
                canUpdate: source.can_update,
                isCard: true,
                source: 'Credit Card',
                type: source.cc_type || 'unknown',
                last4: source.cc_last4 || '****',
                expDate: source.cc_exp_date || '',
                card_type: source.cc_type,
                last_four: source.cc_last4,
                expiry_month: source.cc_exp_date ? Number(source.cc_exp_date.split('/')[0]) : undefined,
                expiry_year: source.cc_exp_date ? Number(source.cc_exp_date.split('/')[1]) : undefined,
                is_default: source.is_default,
              },
            ])
          } else {
            setShowSourceEdit(true)
          }
          setLoading(false)
        } catch (err) {
          setCriticalError(err)
        }
      }
      loadPaymentSources()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time customer prefill; re-running on setter identity would refetch.
  }, [session])

  useEffect(() => {
    if (!checkoutErrors?.length) {
      return
    }
    const shippingFields = ['ship_address1', 'ship_city', 'ship_first_name', 'ship_last_name', 'ship_state', 'ship_zip']
    const billingFields = ['address1', 'city', 'state', 'zip', 'first_name', 'last_name', 'email', 'phone']
    const customerFields = [...billingFields, ...shippingFields] as Array<keyof CustomerFormValues>

    const errorFields: Partial<Record<keyof CustomerFormValues, string>> = {}
    ;(checkoutErrors as any[]).forEach((error: any) => {
      Object.keys(error).forEach(field => {
        if (customerFields.includes(field as keyof CustomerFormValues)) {
          const raw = error[field]
          const message = Array.isArray(raw) ? raw.join(', ') : String(raw)
          errorFields[field as keyof CustomerFormValues] = message
        }
        if (!isSessionUserKnown()) {
          if (billingFields.includes(field)) {
            setShowBillEdit(true)
          }
          if (shippingFields.includes(field)) {
            setShowShipEdit(true)
          }
        }
      })
    })

    const matchedKeys = Object.keys(errorFields) as Array<keyof CustomerFormValues>
    if (matchedKeys.length) {
      matchedKeys.forEach(field => {
        setCustomerError(field, { type: 'server', message: errorFields[field] })
      })
    } else {
      setError(checkoutErrors)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Validation pass keyed to the fields it watches, not to the setters.
  }, [checkoutErrors])

  // Card validation is handled by RHF + Zod schema (mode: 'onChange')
  // using validateCardNumber / validateCardExpiry / validateCardCvc from
  // @utils/cardValidation — same utilities PaymentUpdater uses.

  useEffect(() => {
    setShowSourceEdit(orderPaymentSource === OrderPaymentOptions.New)
  }, [orderPaymentSource])

  // Cybersource microform auto-intercept — imports 'solvpath-microform' and tokenizes
  // card data before submit so raw PAN never leaves the browser. The SDK attaches
  // a handler to #checkout-submit-btn.
  useEffect(() => {
    const publicKey = gateway?.public_key
    if (!publicKey || orderPaymentSource !== OrderPaymentOptions.New) {
      return
    }

    let destroyed = false
    let instance: any = null

    ;(async () => {
      try {
        await import('solvpath-microform')
        if (destroyed) {
          return
        }
        const factory = (window as any).solvpathMicroform
        const submitMode = (window as any).SolvpathSubmitMode?.AUTO_INTERCEPT
        if (typeof factory !== 'function') {
          console.warn('[checkout] solvpath-microform loaded but window.solvpathMicroform missing')
          return
        }
        instance = factory({
          apiKey: publicKey,
          submitId: 'checkout-submit-btn',
          cardFieldId: 'number',
          cvvFieldId: 'cvv_code',
          expiryId: 'expiry',
          expiryMonthId: 'expiry_month',
          expiryYearId: 'expiry_year',
          submitMode,
          errorModal: (message: string) => {
            setCardError('number', { type: 'server', message })
            setError([message])
          },
        })
      } catch (err) {
        console.warn(
          '[checkout] solvpath-microform SDK unavailable — falling back to standard submit. Install `solvpath-microform` to enable tokenization.',
          err
        )
      }
    })()

    return () => {
      destroyed = true
      if (instance?.destroy) {
        instance.destroy()
      }
    }
  }, [gateway, orderPaymentSource, setError, setCardError])

  const completeOrderBlock = (
    <div className={compact ? 'checkout-submit-standalone' : 'form-section'}>
      <button
        type="button"
        id="checkout-submit-btn"
        className="btn-base complete-order-btn"
        onClick={handleCheckout}
        disabled={!isCustomerValid || (orderPaymentSource === OrderPaymentOptions.New && !isCardValid) || isLoading}>
        <Lock size={18} className="complete-order-lock" aria-hidden />
        <span>Complete My Order</span>
        <ArrowRight size={20} className="complete-order-arrow" aria-hidden />
      </button>
    </div>
  )

  return (
    <div className={cn('checkout-container', compact && 'checkout-compact')}>
      {/* Mobile Order Summary — collapsible bar with always-visible grand total below */}
      <div className={cn('mobile-summary', !singleColumn && 'md:hidden')}>
        <button
          type="button"
          className="mobile-summary-toggle"
          onClick={() => setShowOrderSummary(!showOrderSummary)}
          aria-expanded={showOrderSummary}>
          <span className="mobile-summary-toggle-left">
            <ShoppingCart size={18} />
            <span>{showOrderSummary ? 'Hide order summary' : 'Show order summary'}</span>
            {showOrderSummary ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </button>

        {showOrderSummary && (
          <div className="mobile-summary-content">
            {updatedCart.map((item, index) => {
              const max = (item as ExtendedCartItem).campaign_product.max_quantity || 999
              return (
                <div key={index} className="mobile-order-item">
                  <div className="mobile-order-item-img">
                    <img src={getImageUrl(item)} alt={item.campaign_product.name} onError={e => setAltImg(e, item)} />
                    <span className="mobile-qty-badge">{item.quantity}</span>
                  </div>
                  <div className="mobile-order-item-info">
                    <h6
                      className={compact ? undefined : 'product-name-link'}
                      onClick={
                        compact
                          ? undefined
                          : () => {
                              const cp = item.campaign_product as any
                              if (cp?.id) {
                                leave({ productId: cp.id, slug: cp.slug })
                              }
                            }
                      }>
                      {item.campaign_product.name}
                    </h6>
                    {getItemVariants(item).map((variant, vIndex) => (
                      <div key={vIndex} className="mobile-order-item-variant">
                        {variant.name}: {variant.value}
                      </div>
                    ))}
                    <div className="mobile-order-item-controls">
                      <div className="qty-pill mobile-qty-pill">
                        <button
                          type="button"
                          className="no-transform"
                          disabled={item.quantity <= 1 || updatingIndex === index}
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          aria-label="Decrease quantity">
                          <Minus size={13} />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          className="no-transform"
                          disabled={item.quantity >= max || updatingIndex === index}
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          aria-label="Increase quantity">
                          <Plus size={13} />
                        </button>
                      </div>
                      {updatingIndex === index && (
                        <Loader2
                          size={16}
                          className="animate-spin"
                          style={{ color: 'var(--light-text-color, #6b7280)' }}
                        />
                      )}
                      <button
                        type="button"
                        className="no-transform mobile-order-item-remove"
                        onClick={() => handleRemoveClick(index)}
                        disabled={updatingIndex === index}
                        aria-label="Remove item">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="mobile-order-item-price">{formatCurrency(getPrice(item), currency)}</div>
                </div>
              )
            })}

            <div className="mobile-summary-totals">
              <div className="mobile-summary-row">
                <span>Subtotal</span>
                <span>{formatCurrency(totalPrice, currency)}</span>
              </div>
              <div className="mobile-summary-row">
                <span>Shipping fees</span>
                <span>{Number(shipping) > 0 ? formatCurrency(Number(shipping).toString(), currency) : 'Free'}</span>
              </div>
              <div className="mobile-summary-row">
                <span>Taxes</span>
                <span>
                  {Number(tax) > 0 ? formatCurrency(Number(tax).toString(), currency) : formatCurrency('0', currency)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Grand Total always visible — whether expanded or collapsed */}
        <div className="mobile-summary-grand">
          <strong>
            Grand Total <span className="font-normal">(including tax)</span>
          </strong>
          <strong>{formatCurrency(totalPriceIncTax(), currency)}</strong>
        </div>
      </div>

      <div className={cn('flex flex-wrap w-full', !singleColumn && 'md:flex-nowrap md:gap-x-6 lg:gap-x-8')}>
        <div className={cn('w-full', !singleColumn && 'md:w-[55%]')}>
          {/* Main Checkout Form */}
          <div className="checkout-form">
            <CheckoutBillingSection
              customer={customer}
              customerErrors={customerErrors}
              registerCustomer={registerCustomer}
              setCustomerValue={setCustomerValue}
              showBillEdit={showBillEdit}
              isSessionUserKnown={isSessionUserKnown}
              toggleEditing={toggleEditing}
              countryChanged={countryChanged}
              zipFormatHandler={zipFormatHandler}
            />
            <CheckoutShippingSection
              customer={customer}
              customerErrors={customerErrors}
              registerCustomer={registerCustomer}
              setCustomerValue={setCustomerValue}
              showShipEdit={showShipEdit}
              billShipSame={billShipSame}
              setBillShipSame={setBillShipSame}
              isSessionUserKnown={isSessionUserKnown}
              toggleEditing={toggleEditing}
              shipCountryChanged={shipCountryChanged}
              zipFormatHandler={zipFormatHandler}
            />
            {/* Payment Method */}
            <CheckoutPaymentSection
              creditCard={creditCard}
              cardErrors={cardErrors}
              setCardValue={setCardValue}
              cardBrand={cardBrand}
              showSourceEdit={showSourceEdit}
              orderPaymentSource={orderPaymentSource}
              setOrderPaymentSource={setOrderPaymentSource}
              paymentSource={paymentSource}
              paymentSourceOptions={paymentSourceOptions}
              isSessionUserKnown={isSessionUserKnown}
              toggleEditing={toggleEditing}
              showSatisfactionGuarantee={showSatisfactionGuarantee}
              guaranteeDays={guaranteeDays}
            />
            {/* Complete Order Button — only an embedded two-column host moves it out */}
            {!(compact && !singleColumn) && completeOrderBlock}
          </div>
        </div>

        <div className={cn('w-full', !singleColumn && 'md:w-[45%]')}>
          {/* Desktop Order Summary */}
          <div className={cn('checkout-order-summary', singleColumn ? 'hidden' : 'hidden md:block')}>
            <h4 className="order-summary-text mb-4">Order Summary</h4>

            <div className="order-items">
              {updatedCart.map((item, index) => {
                const max = (item as ExtendedCartItem).campaign_product.max_quantity || 999
                return (
                  <div key={index} className="order-item flex items-start gap-4 mb-5">
                    <div className="order-item-img-wrap">
                      <div className="order-item-img">
                        <img
                          src={getImageUrl(item)}
                          alt={item.campaign_product.name}
                          onError={e => setAltImg(e, item)}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <span className="order-item-qty-badge">{item.quantity}</span>
                    </div>
                    <div className="product-details grow min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h6
                            className={cn('product-name leading-tight m-0', !compact && 'product-name-link')}
                            onClick={
                              compact
                                ? undefined
                                : () => {
                                    const cp = item.campaign_product as any
                                    if (cp?.id) {
                                      leave({ productId: cp.id, slug: cp.slug })
                                    }
                                  }
                            }>
                            {item.campaign_product.name}
                          </h6>
                          {getItemVariants(item).map((variant, vIndex) => (
                            <div
                              key={vIndex}
                              className="variant-info"
                              style={{ color: 'var(--light-text-color, #6b7280)' }}>
                              {variant.name}: {variant.value}
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveClick(index)}
                          className="no-transform shrink-0 p-1 text-red-400 hover:text-red-600 transition-colors"
                          style={{ minWidth: 0, minHeight: 0, border: 'none', background: 'none' }}
                          title="Remove">
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <div className="qty-pill inline-flex items-center rounded-md overflow-hidden">
                            <button
                              type="button"
                              className="no-transform w-7 h-7 flex items-center justify-center hover:bg-black/5 disabled:opacity-30 transition-colors"
                              style={{ minWidth: 0, minHeight: 0, border: 'none', background: 'none' }}
                              disabled={item.quantity <= 1 || updatingIndex === index}
                              onClick={() => updateQuantity(index, item.quantity - 1)}>
                              <Minus size={12} />
                            </button>
                            <div
                              className="w-8 h-7 flex items-center justify-center text-sm font-medium border-x"
                              style={{ borderColor: 'var(--border-color, #d1d5db)', color: 'var(--text-color)' }}>
                              {item.quantity}
                            </div>
                            <button
                              type="button"
                              className="no-transform w-7 h-7 flex items-center justify-center hover:bg-black/5 disabled:opacity-30 transition-colors"
                              style={{ minWidth: 0, minHeight: 0, border: 'none', background: 'none' }}
                              disabled={item.quantity >= max || updatingIndex === index}
                              onClick={() => updateQuantity(index, item.quantity + 1)}>
                              <Plus size={12} />
                            </button>
                          </div>
                          {updatingIndex === index && (
                            <Loader2
                              size={16}
                              className="animate-spin"
                              style={{ color: 'var(--light-text-color, #6b7280)' }}
                            />
                          )}
                        </div>
                        <span className="text-base font-bold" style={{ color: 'var(--text-color)' }}>
                          {formatCurrency(getPrice(item), currency)}
                        </span>
                      </div>
                      {item.quantity >= max && (
                        <p className="text-xs text-red-500 mt-1 m-0">*Maximum order limit of {max}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <hr />

            <div className="checkout-totals">
              <div className="checkout-totals-line flex justify-between">
                <span>Subtotal</span>
                <span className="checkout-totals-price">{formatCurrency(totalPrice, currency)}</span>
              </div>
              <div className="checkout-totals-line flex justify-between">
                <span>Shipping fees</span>
                <span className="checkout-totals-price">
                  {Number(shipping) > 0 ? formatCurrency(Number(shipping).toString(), currency) : 'Free'}
                </span>
              </div>
              <div className="checkout-totals-line flex justify-between">
                <span>Taxes</span>
                <span className="checkout-totals-price">
                  {Number(tax) > 0 ? formatCurrency(Number(tax).toString(), currency) : formatCurrency('0', currency)}
                </span>
              </div>
              <hr />
              <div className="checkout-totals-line is-grand-total flex justify-between">
                <span>
                  <strong>Grand Total</strong> (including tax)
                </span>
                <strong>{formatCurrency(totalPriceIncTax(), currency)}</strong>
              </div>
            </div>
          </div>

          {compact && !singleColumn && completeOrderBlock}

          {/* Secure Connection badge — below Order Summary card */}
          <div className="hidden md:flex justify-center mt-4">
            <img
              src="/assets/icons/Secure-Connection.svg"
              alt="Secure Connection"
              className="w-[200px] lg:w-[240px] opacity-90"
            />
          </div>
        </div>
      </div>

      {/* Remove item confirmation */}
      <Modal
        isOpen={removeIndex !== null}
        onClose={() => {
          if (!removing) {
            setRemoveIndex(null)
          }
        }}
        title="Remove item?"
        size="sm">
        <div className="text-center py-2">
          <p className="text-base mb-6" style={{ color: 'var(--text-color)' }}>
            {removeIndex !== null && updatedCart.length <= 1
              ? 'This is the last item in your cart. Removing it will clear your cart and take you back to products.'
              : `Remove "${removeIndex !== null ? updatedCart[removeIndex]?.campaign_product?.name : ''}" from your order?`}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <button
              type="button"
              onClick={() => setRemoveIndex(null)}
              disabled={removing}
              className="btn-interactive flex-1 py-3 text-base font-semibold rounded-xl"
              style={{
                border: '2px solid var(--border-color, #d1d5db)',
                color: 'var(--text-color)',
                backgroundColor: 'transparent',
              }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmRemove}
              disabled={removing}
              className="btn-interactive flex-1 py-3 text-base font-semibold rounded-xl text-white flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--color-danger)' }}>
              {removing && <Loader2 size={16} className="animate-spin" />}
              {removing ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Checkout
